import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useDebounced } from '@/lib/catalogue';
import { useCustomers } from '@/lib/orders';
import { useSession } from '@/lib/session';
import { formatMoney } from '@/lib/utils';

type CouponLine = {
  lineId: number;
  lineCode: string;
  lineName: string;
  tranche: string;
  purchases: string;
  entitled: number;
  remis: number;
  remaining: number;
  claimable: boolean;
};
type CouponsData = { customer: { id: number; name: string; custNum: string }; lines: CouponLine[] };

/**
 * Coupons de tirage d'un client, par ligne. Au comptoir (readOnly=false) : compteur
 * +/− pour marquer les coupons physiquement remis (dus / remis / restant). En
 * consultation (readOnly=true) : lecture seule.
 */
export default function CouponsClaims({
  readOnly = false,
  customerId: controlledId,
}: {
  readOnly?: boolean;
  customerId?: number | null;
}) {
  // Mode « contrôlé » : le client vient du parent (comptoir à recherche unique).
  const controlled = controlledId !== undefined;
  // Comptoir / super rep : on ne fait QUE compter les coupons remis — pas de retrait.
  // Seul l'admin peut corriger (retirer un coupon).
  const { session } = useSession();
  const peutRetirer = session?.user.role === 'ADMIN';
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);
  const [ownCustomerId, setOwnCustomerId] = useState<number | null>(null);
  const customerId = controlled ? (controlledId ?? null) : ownCustomerId;
  const qc = useQueryClient();

  const coupons = useQuery<CouponsData>({
    queryKey: ['coupons', customerId],
    queryFn: () => api(`/api/coupons/${customerId}`),
    enabled: customerId !== null,
  });

  const setCoupon = useMutation({
    mutationFn: ({ lineId, action }: { lineId: number; action: 'add' | 'remove' }) =>
      action === 'add'
        ? api<{ lines: CouponLine[] }>('/api/coupons', {
            method: 'POST',
            body: JSON.stringify({ customerId, lineId }),
          })
        : api<{ lines: CouponLine[] }>(`/api/coupons/${customerId}/${lineId}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      qc.setQueryData<CouponsData>(['coupons', customerId], (old) =>
        old ? { ...old, lines: data.lines } : old,
      );
    },
  });

  if (!controlled && customerId === null) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Coupons de tirage</h2>
          <p className="text-muted-foreground text-sm">
            Cherchez un client pour voir ses coupons de participation
            {readOnly ? '.' : ' et remettre ceux qui lui sont dus.'}
          </p>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nom, n° de client ou ville…"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          className="border-input bg-background h-12 w-full max-w-lg rounded-lg border px-3 text-base"
        />

        {recherche && (
          <div className="grid max-w-lg gap-2">
            {clients.data?.customers.map((c) => (
              <button
                key={c.id}
                onClick={() => setOwnCustomerId(c.id)}
                className="bg-card hover:border-primary/50 flex items-center gap-3 rounded-lg border p-3 text-left"
              >
                <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                  {c.custNum}
                </span>
                <span className="font-medium">{c.name}</span>
              </button>
            ))}
            {!clients.isPending && clients.data?.customers.length === 0 && (
              <p className="text-muted-foreground text-sm">Aucun client trouvé.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  const data = coupons.data;
  const pendingLine = setCoupon.isPending ? setCoupon.variables?.lineId : null;
  const totalDus = data?.lines.reduce((a, l) => a + l.entitled, 0) ?? 0;

  return (
    <div className="space-y-5">
      {/* En mode contrôlé, l'entête client (nom + changer de client) est géré par le parent. */}
      {!controlled && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{data?.customer.name ?? 'Client'}</h2>
            {data && (
              <p className="text-muted-foreground font-mono text-sm">{data.customer.custNum}</p>
            )}
          </div>
          <button
            onClick={() => {
              setOwnCustomerId(null);
              setQ('');
            }}
            className="border-input rounded-lg border px-4 py-2 text-sm font-medium"
          >
            ← Autre client
          </button>
        </div>
      )}

      {coupons.isPending ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : !data || data.lines.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <p className="font-medium">Aucun coupon</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Ce client n'a pas encore atteint de tranche donnant un coupon.
          </p>
        </div>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">
            {totalDus} coupon{totalDus > 1 ? 's' : ''} dû{totalDus > 1 ? 's' : ''} au total
          </p>
          {data.lines.map((l) => (
            <section key={l.lineId} className="bg-card rounded-xl border p-5">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-semibold">
                  {l.lineCode} <span className="text-muted-foreground font-normal">— {l.lineName}</span>
                </h3>
                <span className="text-muted-foreground text-sm">
                  {formatMoney(l.purchases)} d'achats · 1 coupon / {formatMoney(l.tranche)}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span>
                    Dus : <b className="tabular-nums">{l.entitled}</b>
                  </span>
                  <span>
                    Remis : <b className="tabular-nums text-green-700 dark:text-green-400">{l.remis}</b>
                  </span>
                  <span>
                    Restant : <b className="tabular-nums">{l.remaining}</b>
                  </span>
                </div>
                {!readOnly && (
                  <div className="flex shrink-0 items-center gap-2">
                    {/* Retrait réservé à l'admin : le comptoir/super rep ne fait que
                        compter les coupons remis, sans pouvoir en enlever. */}
                    {peutRetirer && (
                      <button
                        type="button"
                        aria-label="Retirer un coupon remis"
                        onClick={() => setCoupon.mutate({ lineId: l.lineId, action: 'remove' })}
                        disabled={l.remis === 0 || (setCoupon.isPending && pendingLine === l.lineId)}
                        className="border-input h-9 w-9 rounded-lg border text-lg font-bold disabled:opacity-30"
                      >
                        −
                      </button>
                    )}
                    <span className="w-6 text-center tabular-nums">{l.remis}</span>
                    <button
                      type="button"
                      aria-label="Remettre un coupon"
                      onClick={() => setCoupon.mutate({ lineId: l.lineId, action: 'add' })}
                      disabled={!l.claimable || (setCoupon.isPending && pendingLine === l.lineId)}
                      title={!l.claimable ? 'Aucun coupon restant' : undefined}
                      className="border-primary/40 text-primary h-9 w-9 rounded-lg border text-lg font-bold disabled:opacity-30"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </section>
          ))}
        </>
      )}

      {setCoupon.isError && (
        <p className="text-destructive text-sm font-medium">{(setCoupon.error as Error).message}</p>
      )}
    </div>
  );
}
